import {
  addDays,
  addMonths,
  addYears,
  differenceInMonths,
  differenceInYears,
  isBefore,
  isDate,
  isEqual,
  isLastDayOfMonth,
  isLeapYear,
  isValid,
  lastDayOfMonth,
  lightFormat,
  parse,
  parseISO,
  setMonth,
  subYears
} from 'date-fns';

export interface IDateDiff {
  years: number;
  months: number;
  days: number;
}

export type DateParam = Date | string | undefined | null;

// Format hh:mm
export const TIME_REGEX = /^([01]\d|2[0-3]):?([0-5]\d)$/;

/**
 * Validate Date
 *
 * @example
 * // returns true
 * isValidISODate('2013-12-02');
 * @example
 * // returns false
 * isValidISODate('2013-13-40');
 *
 */
export function isValidISODate(dateString: string): boolean {
  if (!dateString || dateString.length < 10) {
    return false;
  }
  const jsDate = isoDateStringToDate(dateString);
  if (!isValid(jsDate)) {
    return false;
  }
  return isDate(jsDate);
}

export function isValidTimeFormat(timeString: string): boolean {
  return TIME_REGEX.test(timeString);
}

/**
 *
 * @param date - Geburtsdatum als Date oder string im ISO Format (z.B. 1975-03-20)
 * @param now - Ausgangsdatum fuer die Berechnung wenn es uebergeben wird oder aktuelles Tagesdatum
 */
export function getAlterForGeburtstag(date: Date | string, now: Date = today()): number {
  const jsGeburtsdatum = dateStringOrDateToDate(date);
  return differenceInYears(now, jsGeburtsdatum);
}
export function getAlterInMonatenForGeburtstag(date: Date | string, now: Date = today()): number {
  const jsGeburtsdatum = dateStringOrDateToDate(date);
  return differenceInMonths(now, jsGeburtsdatum);
}

export function getBeginningOfLastMonth(date: Date = new Date()): Date {
  return new Date(date.getFullYear(), date.getMonth() - 1, 1);
}

export function getBeginningOfNextMonth(date: Date = new Date()): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 1);
}

export function getBeginningOfThisMonth(date: Date = new Date()): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function getBeginningOfThisYear(date: Date = new Date()): Date {
  return new Date(date.getFullYear(), 0, 1);
}

export function getBeginningOfLastYear(date: Date = new Date()): Date {
  return new Date(date.getFullYear() - 1, 0, 1);
}

export function getBeginningOfNextMonthAsIsoDate(date: Date = new Date()): string {
  return dateToIsoDateString(getBeginningOfNextMonth(date));
}

export function getBeginningOfThisMonthAsIsoDate(date: Date = new Date()): string {
  return dateToIsoDateString(getBeginningOfThisMonth(date));
}

/**
 * Jahr im Format 2019
 *
 */
export function getYearFromDate(date: Date | string): string {
  const jsDate = dateStringOrDateToDate(date);
  return lightFormat(jsDate, 'yyyy');
}

/**
 * Datum im Format 24.12.2019
 *
 */
export function getFormattedDateString(date: Date | string): string {
  const jsDate = dateStringOrDateToDate(date);
  if (!jsDate) {
    return null;
  }
  return lightFormat(jsDate, 'dd.MM.yyyy');
}

/**
 * Datum und Zeit im Format 24.12.2019 16:34
 *
 */
export function getFormattedTimeString(date: Date | string): string {
  const jsDate = dateStringOrDateToDate(date);
  if (!jsDate) {
    return null;
  }
  return lightFormat(jsDate, 'HH:mm');
}

/**
 * Datum und Zeit im Format 24.12.2019 16:34
 *
 */
export function getFormattedDateTimeString(date: Date | string, precise = false): string {
  const format = precise ? 'long' : 'short';
  return getFormattedDateTimeStringFor(date, format);
}

export function getFormattedDateTimeStringFor(
  date: Date | string,
  format: 'short' | 'medium' | 'long' = 'short'
): string {
  const jsDate = dateStringOrDateToDate(date);
  if (!jsDate) {
    return null;
  }
  if (format === 'long') {
    return lightFormat(jsDate, 'dd.MM.yyyy HH:mm:ss.SSS');
  } else if (format === 'medium') {
    return lightFormat(jsDate, 'dd.MM.yyyy HH:mm:ss');
  } else {
    return lightFormat(jsDate, 'dd.MM.yyyy HH:mm');
  }
}

/**
 * Konvertiert einen ISO String (2019-11-24) oder ein JS-Date zu einem JS-Date
 *
 */
export function dateStringOrDateToDate(date: string | Date): Date {
  if (isDate(date)) {
    return date;
  } else if (typeof date === 'string') {
    return isoDateStringToDate(date);
  }
}

/**
 * Konvertiert einen ISO String (2019-11-24) zu einem JS-Date
 *
 */
export function isoDateStringToDate(dateString: string): Date {
  if (!dateString) {
    return;
  }

  if (dateString.length === 16) {
    return parse(dateString, 'yyyy-MM-dd HH:mm', new Date());
  } else {
    return parseISO(dateString);
  }
}

/**
 * Konvertiert ein JS-Date zu einem ISO-Date-String (2019-12-24)
 *
 */
export function dateToIsoDateString(date: Date = new Date()): string {
  if (date === null || date === undefined) {
    return null;
  }
  return lightFormat(date, 'yyyy-MM-dd');
}

/**
 * Gibt Heute als Date ohne Time zurück
 *
 */
export function today(): Date {
  return isoDateStringToDate(dateToIsoDateString(new Date()));
}

/**
 * Konvertiert ein JS-Date zu einem ISO-Date-Time-String (2019-12-24 16:34)
 *
 */
export function dateToIsoDateTimeString(date: Date = new Date(), precise = false): string {
  if (precise) {
    return lightFormat(date, 'yyyy-MM-dd HH:mm:ss.SSS');
  } else {
    return lightFormat(date, 'yyyy-MM-dd HH:mm');
  }
}

export function yearToDate(year: string | number): Date {
  const yearString = typeof year === 'number' ? year.toString() : year;
  return parse(yearString, 'yyyy', new Date());
}

export function isDate1GreaterThenDate2(date1: string | Date, date2: string | Date): boolean {
  const realDate1 = dateStringOrDateToDate(date1);
  const realDate2 = dateStringOrDateToDate(date2);
  return isBefore(realDate2, realDate1);
}

export function isDate1GreaterThenOrEqualDate2(date1: string | Date, date2: string | Date): boolean {
  const realDate1 = dateStringOrDateToDate(date1);
  const realDate2 = dateStringOrDateToDate(date2);
  return isBefore(realDate2, realDate1) || isEqual(realDate2, realDate1);
}

export function isSchaltjahr(year: number): boolean {
  const jsDate = yearToDate(year);
  return isLeapYear(jsDate);
}

export function getYearPrefixedWithJahrhundert(jahrezehntPart: string, now: Date = new Date()): string {
  const currentYearPart = Number(now.getFullYear().toString().slice(2, 4));
  const selectedYearPart = Number(jahrezehntPart);

  if (selectedYearPart > currentYearPart) {
    return '19' + jahrezehntPart;
  } else {
    return '20' + jahrezehntPart;
  }
}

export function isoTo6DigitDate(dateString: string): string {
  const jsDate = isoDateStringToDate(dateString);
  return lightFormat(jsDate, 'ddMMyy');
}

// TODO: date-fns difference functions nutzen
export function dateDifferenceToString(dateDiff: IDateDiff): string {
  function getText(value: number, dateUnits: string[]): string | undefined {
    if (!value) {
      return undefined;
    }
    const text = value === 1 ? dateUnits[0] : dateUnits[1];
    return `${value} ${text}`;
  }

  const years = getText(dateDiff.years, ['Jahr', 'Jahre']);
  const months = getText(dateDiff.months, ['Monat', 'Monate']);
  const days = getText(dateDiff.days, ['Tag', 'Tage']);

  return [years, months, days].filter((value) => !!value).join(', ');
}

export function getDateDifferenceAsString(startDateParam: string | Date, endDateParam: string | Date): string {
  return dateDifferenceToString(getDateDifference(startDateParam, endDateParam));
}

export function getDateDifference(startDateParam: string | Date, endDateParam: string | Date): IDateDiff {
  const startDate: Date = dateStringOrDateToDate(startDateParam);
  const endDate: Date = dateStringOrDateToDate(endDateParam);

  endDate.setDate(endDate.getDate() + 1);

  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth();
  const startDay = startDate.getDate();

  const endYear = endDate.getFullYear();
  const endMonth = endDate.getMonth();
  const endDay = endDate.getDate();

  // We calculate February based on end year as it might be a leep year which might influence the number of days.
  const february = (endYear % 4 === 0 && endYear % 100 !== 0) || endYear % 400 === 0 ? 29 : 28;
  const daysOfMonth = [31, february, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  const startDateNotPassedInEndYear = endMonth < startMonth || (endMonth === startMonth && endDay < startDay);
  const years = endYear - startYear - (startDateNotPassedInEndYear ? 1 : 0);

  const months = (12 + endMonth - startMonth - (endDay < startDay ? 1 : 0)) % 12;

  // (12 + ...) % 12 makes sure index is always between 0 and 11
  const days = startDay <= endDay ? endDay - startDay : daysOfMonth[(12 + endMonth - 1) % 12] - startDay + endDay;

  return {
    years: years,
    months: months,
    days: days
  };
}

export function addMonthsTo(dateParam: string | Date, amount: number): Date {
  const date = dateStringOrDateToDate(dateParam);
  const newDate = addMonths(date, amount);

  if (isLastDayOfMonth(date) && !isLastDayOfMonth(newDate)) {
    const correcturDate = lastDayOfMonth(newDate);
    newDate.setDate(correcturDate.getDate());
  }

  return newDate;
}

export function addYearsTo(dateParam: string | Date, amount: number): Date {
  const date = dateStringOrDateToDate(dateParam);
  return addYears(date, amount);
}

export function addDaysTo(dateParam: string | Date, amount: number): Date {
  const date = dateStringOrDateToDate(dateParam);
  return addDays(date, amount);
}

export function setToMonthAndLastDay(dateParam: string | Date, month: number): Date {
  let date = dateStringOrDateToDate(dateParam);
  date = setMonth(date, month);
  date = lastDayOfMonth(date);
  return date;
}

export function getMonthSum(dateDiff: IDateDiff): number {
  return (dateDiff.years || 0) * 12 + (dateDiff.months || 0);
}

export function getMonthNameFromNumberValue(month: number): string {
  const date = new Date();
  date.setDate(1);
  date.setMonth(month);
  return date.toLocaleDateString('de-AT', { month: 'long' });
}

export function getMonthAndYearStringFromDateOrString(dateString: string | Date | undefined): string {
  if (dateString === undefined) {
    return 'Kein Datum';
  }
  const date = dateStringOrDateToDate(dateString);
  return `${getMonthNameFromNumberValue(date.getMonth())} ${date.getFullYear()}`;
}

export function getISODateStringIgnoringTimezone(date: Date): string {
  return date ? getDateIgnoringTimezone(date).toISOString().slice(0, 10) : null;
}

export function getDateIgnoringTimezone(date: Date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
}

export function isGiltVonBisPruefungOk(giltVon?: DateParam, giltBis?: DateParam, stichTag = new Date()): boolean {
  let result = true;
  if (giltVon || giltBis) {
    // Wenn giltVon definiert ist, prüfen, ob heute nach giltVon liegt (oder gleich ist)
    if (giltVon) {
      const giltVonDatum = dateStringOrDateToDate(giltVon);
      result = stichTag >= giltVonDatum;
    }

    // Wenn giltBis definiert ist, prüfen, ob heute vor giltBis liegt (oder gleich ist)
    if (result && giltBis) {
      const giltBisDatum = dateStringOrDateToDate(giltBis);
      result = stichTag <= giltBisDatum;
    }
  }
  return result;
}

/**
 * Generiert ein Geburtsdatum für eine Person mit dem gewünschten Alter.
 *
 * @param age   Wie viele Jahre das zurückgegebene Datum in der Vergangenheit liegen soll.
 * @param date  Optionales "Start"-Datum, von dem das Alter subtrahiert wird.
 */
export function getDateForAlter(age: number, date: Date = today()): Date {
  return subYears(date, age);
}
